import { getCurrentInstance } from 'vue';

/**
 * Helper function for Vue 3 i18n translations with safe fallback
 *
 * In Vue 3 with vue-i18n v9, i18n.global.t() can return undefined if:
 * - The i18n instance isn't initialized yet
 * - The key doesn't exist and there's no fallback
 * - The injected i18n is undefined
 *
 * This helper ensures we always return a string, preventing "undefined" from
 * being rendered in templates.
 *
 * Use this when you have the i18n instance (e.g. inject('i18n') in Vue, or
 * imported I18n in Stimulus): t(i18n, 'key', params, fallback)
 *
 * @param {Object} i18n - The injected i18n instance
 * @param {String} key - The translation key
 * @param {Object} params - Optional parameters for the translation
 * @param {String} fallback - Optional fallback string (defaults to empty string)
 * @returns {String} The translated string or fallback
 */
export function t(i18n, key, params = {}, fallback = '') {
  if (!i18n?.global?.t) {
    return fallback;
  }

  const result = i18n.global.t(key, params);
  return result != null ? result : fallback;
}

/**
 * Vue composable for i18n translation. Resolves i18n from the current
 * component instance, so you don't pass it. Optional prefix for scoped keys.
 *
 * Usage:
 *   const { t } = useTranslation()
 *   t('some.key')
 *
 *   const { t } = useTranslation('screens.identity.account.preferences')
 *   t('field_name')  // -> screens.identity.account.preferences.field_name
 */
export function useTranslation(prefix = '') {
  const instance = getCurrentInstance();
  const proxy = instance?.proxy;

  const translate = (key, values) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    const i18n = proxy?.$i18n ?? proxy?.$root?.$i18n;

    if (i18n?.global?.t) {
      return t(i18n, fullKey, values ?? {}, fullKey);
    }
    if (i18n?.t) {
      const result = i18n.t(fullKey, values);
      return result != null ? result : fullKey;
    }
    return fullKey;
  };

  return { t: translate };
}
